// Путь: D:\QR\QR-WEB\dashboard\src\components\User\ClaimVoucher.jsx
// Название: Claim Voucher - Professional Design
// Версия: 7.0.0
// Дата: 27.01.2026

import React, { useState, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Tesseract from 'tesseract.js';

const API_URL = process.env.REACT_APP_API_URL || 'https://qr-money.onrender.com';

const ClaimVoucher = () => {
  const [searchParams] = useSearchParams();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [decoding, setDecoding] = useState(false);
  const [decodingStatus, setDecodingStatus] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(null);
  const [giftInfo, setGiftInfo] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const fileInputRef = useRef(null);

  const token = localStorage.getItem('authToken');

  useEffect(() => {
    const giftParam = searchParams.get('gift') || searchParams.get('code');
    if (giftParam) {
      setCode(giftParam);
      checkGift(giftParam);
    }
  }, [searchParams]);

  const extractCodeFromText = (text) => {
    const pattern = /[A-Z0-9]{4}-[A-Z0-9]{4}/gi;
    const matches = text.match(pattern);
    if (matches && matches.length > 0) {
      return matches[0].toUpperCase();
    }
    return null;
  };

  const recognizeWithOCR = async (file) => {
    setDecodingStatus('OCR распознавание...');
    try {
      const result = await Tesseract.recognize(file, 'eng', {
        logger: (m) => {
          if (m.status === 'recognizing text') {
            setDecodingStatus('OCR: ' + Math.round(m.progress * 100) + '%');
          }
        }
      });
      return extractCodeFromText(result.data.text);
    } catch (err) {
      console.error('[OCR] Error:', err);
      return null;
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Выберите изображение PNG или JPEG');
      return;
    }

    const imageUrl = URL.createObjectURL(file);
    setUploadedImage(imageUrl);
    setError('');
    setDecoding(true);
    setDecodingStatus('Поиск данных...');
    setGiftInfo(null);

    try {
      const formData = new FormData();
      formData.append('image', file);
      const res = await axios.post(API_URL + '/api/vouchers/decode-image', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (res.data.success && res.data.data) {
        const data = res.data.data;
        const foundCode = data.shortCode || data.voucherToken || data.code || data.token;
        
        if (foundCode) {
          setCode(foundCode);
          setDecoding(false);
          setDecodingStatus('');
          await checkGift(foundCode);
          return;
        }
      }
    } catch (err) {
      console.log('[STEGO] Failed, trying brand-gifts decode...', err.message);
    }

    try {
      setDecodingStatus('Проверка AI подарка...');
      const formData = new FormData();
      formData.append('image', file);
      const res = await axios.post(API_URL + '/api/brand-gifts/decode', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (res.data.success && res.data.hasGift && res.data.gift) {
        setGiftInfo({ ...res.data.gift, type: res.data.giftType || 'brand_gift' });
        setCode(res.data.gift.token);
        setDecoding(false);
        setDecodingStatus('');
        return;
      }
    } catch (err) {
      console.log('[BRAND-GIFT] Failed, trying OCR...', err.message);
    }

    try {
      const ocrCode = await recognizeWithOCR(file);
      
      if (ocrCode) {
        setCode(ocrCode);
        setDecoding(false);
        setDecodingStatus('');
        await checkGift(ocrCode);
        return;
      }
      
      setError('Не удалось распознать подарок. Убедитесь, что это оригинальная картинка.');
      setUploadedImage(null);
    } catch (err) {
      setError('Ошибка распознавания');
      setUploadedImage(null);
    } finally {
      setDecoding(false);
      setDecodingStatus('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const checkGift = async (inputCode) => {
    const codeToCheck = inputCode || code;
    if (!codeToCheck.trim()) return;

    setError('');
    setGiftInfo(null);
    setLoading(true);

    const isLongToken = codeToCheck.length > 20;
    const isShortCode = /^[A-Z0-9]{4}-[A-Z0-9]{4}$/i.test(codeToCheck);

    try {
      if (isLongToken) {
        try {
          const res = await axios.get(API_URL + '/api/brand-gifts/info/' + codeToCheck);
          if (res.data.success && res.data.gift) {
            setGiftInfo({ ...res.data.gift, type: 'brand_gift' });
            setLoading(false);
            return;
          }
        } catch (e) {}

        try {
          const res = await axios.get(API_URL + '/api/vouchers/' + codeToCheck + '/info', {
            headers: token ? { Authorization: 'Bearer ' + token } : {}
          });
          if (res.data.success) {
            setGiftInfo({ ...res.data.data, type: 'voucher' });
            setLoading(false);
            return;
          }
        } catch (e) {}
      }

      if (isShortCode) {
        try {
          const res = await axios.get(API_URL + '/api/brand-gifts/code/' + codeToCheck);
          if (res.data.success && res.data.gift) {
            setGiftInfo({ ...res.data.gift, type: 'brand_gift' });
            setLoading(false);
            return;
          }
        } catch (e) {}

        try {
          const res = await axios.get(API_URL + '/api/campaigns/gift/' + codeToCheck);
          if (res.data.success) {
            setGiftInfo({ ...res.data.data, type: 'gift' });
            setLoading(false);
            return;
          }
        } catch (e) {}

        try {
          const res = await axios.get(API_URL + '/api/vouchers/code/' + codeToCheck + '/info', {
            headers: token ? { Authorization: 'Bearer ' + token } : {}
          });
          if (res.data.success) {
            setGiftInfo({ ...res.data.data, type: 'voucher' });
            setLoading(false);
            return;
          }
        } catch (e) {}
      }

      setError('Подарок не найден');
    } catch (err) {
      setError(err.response?.data?.error || 'Подарок не найден');
    } finally {
      setLoading(false);
    }
  };

  const handleClaim = async () => {
    if (!token) {
      setError('Войдите в аккаунт для активации подарка');
      return;
    }

    setError('');
    setLoading(true);

    try {
      let res;
      
      if (giftInfo.type === 'referral_gift' || giftInfo.type === 'referral') {
        res = await axios.post(
          API_URL + '/api/referral-gifts/activate',
          { token: code },
          { headers: { Authorization: 'Bearer ' + token } }
        );
      } else if (giftInfo.type === 'brand_gift') {
        res = await axios.post(
          API_URL + '/api/brand-gifts/activate',
          { token: code },
          { headers: { Authorization: 'Bearer ' + token } }
        );
      } else if (giftInfo.type === 'gift') {
        res = await axios.post(
          API_URL + '/api/campaigns/gift/' + code + '/claim',
          {},
          { headers: { Authorization: 'Bearer ' + token } }
        );
      } else {
        const isLongToken = code.length > 20;
        const endpoint = isLongToken ? '/api/vouchers/claim' : '/api/vouchers/claim-code';
        const body = isLongToken ? { voucherToken: code } : { fallbackCode: code };

        res = await axios.post(API_URL + endpoint, body, {
          headers: { Authorization: 'Bearer ' + token }
        });
      }

      if (res.data.success) {
        const successData = res.data.data || res.data;
        const claimedAmount = successData.gift?.spendAmount || successData.gift?.totalAmount || successData.amount || successData.claimedAmount || giftInfo?.total_amount || giftInfo?.amount || 0;
        setSuccess({
          amount: claimedAmount,
          type: giftInfo.type
        });
        setGiftInfo(null);
        setCode('');
        setUploadedImage(null);
      } else {
        setError(res.data.error || 'Ошибка активации');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Ошибка активации подарка');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setCode('');
    setError('');
    setSuccess(null);
    setGiftInfo(null);
    setUploadedImage(null);
  };

  const isClaimable = giftInfo && (
    (giftInfo.type === 'brand_gift' || giftInfo.type === 'referral_gift' || giftInfo.type === 'referral')
      ? (giftInfo.status === 'ready' || giftInfo.status === 'active' || giftInfo.status === 'archived_brand')
      : giftInfo.type === 'gift' 
        ? giftInfo.is_claimable 
        : giftInfo.status === 'active'
  );

  const getDisplayAmount = () => {
    if (!giftInfo) return '0.00';
    const amount = giftInfo.total_amount || giftInfo.amount || giftInfo.totalAmount || 0;
    return parseFloat(amount).toFixed(2);
  };

  const getStatusText = (status) => {
    const map = {
      'ready': 'Доступен',
      'active': 'Доступен',
      'archived_brand': '🎁 DARY Universal',
      'claimed': 'Активирован',
      'used': 'Использован',
      'expired': 'Истёк'
    };
    return map[status] || status;
  };

  const getStatusStyle = (status) => {
    if (status === 'ready' || status === 'active') {
      return { background: '#dcfce7', color: '#166534' };
    }
    if (status === 'archived_brand') {
      return { background: '#f3e8ff', color: '#7c3aed' };
    }
    if (status === 'claimed' || status === 'used') {
      return { background: '#f3f4f6', color: '#6b7280' };
    }
    if (status === 'expired') {
      return { background: '#fef2f2', color: '#dc2626' };
    }
    return { background: '#f3f4f6', color: '#6b7280' };
  };

  return (
    <div style={s.page}>
      <div style={s.container}>
        {error && <div style={s.error}>{error}</div>}

        {success ? (
          <div style={s.successBlock}>
            <div style={s.successIcon}>✓</div>
            <h3 style={s.successTitle}>Подарок активирован!</h3>
            <p style={s.successAmount}>+{parseFloat(success.amount).toFixed(2)} DAR</p>
            <p style={s.successText}>Средства зачислены на ваш баланс</p>
            <button onClick={resetForm} style={s.btnPrimary}>
              Активировать ещё
            </button>
          </div>
        ) : (
          <>
            {/* Upload Section */}
            <div style={s.uploadSection}>
              <h2 style={s.uploadTitle}>🎁 Получить подарок</h2>
              <p style={s.uploadDesc}>
                Загрузите картинку с подарком — мы автоматически распознаем и начислим средства
              </p>
              
              <label style={s.uploadLabel}>
                <input
                  type="file"
                  accept="image/png,image/jpeg,image/webp"
                  onChange={handleImageUpload}
                  ref={fileInputRef}
                  disabled={decoding || loading}
                  style={{ display: 'none' }}
                />
                <div style={{...s.uploadBtn, ...(decoding ? s.uploadBtnDecoding : {})}}>
                  {decoding ? (
                    <>
                      <span style={s.spinner}></span>
                      {decodingStatus || 'Распознавание...'}
                    </>
                  ) : (
                    <>📷 Загрузить картинку</>
                  )}
                </div>
              </label>
            </div>

            {/* Gift Preview */}
            {giftInfo && (
              <div style={s.giftCard}>
                {(giftInfo.image_url || uploadedImage) && (
                  <div style={s.giftImageWrapper}>
                    <img 
                      src={giftInfo.image_url || uploadedImage} 
                      alt="Gift" 
                      style={s.giftImage}
                    />
                  </div>
                )}
                
                <div style={s.giftInfo}>
                  <div style={s.giftAmount}>{getDisplayAmount()}</div>
                  <div style={s.giftCurrency}>DAR</div>
                  <div style={{...s.giftStatus, ...getStatusStyle(giftInfo.status)}}>
                    {getStatusText(giftInfo.status)}
                  </div>
                </div>

                {isClaimable ? (
                  <button 
                    onClick={handleClaim} 
                    disabled={loading || !token}
                    style={{...s.btnClaim, ...(loading ? s.btnDisabled : {})}}
                  >
                    {loading ? 'Активация...' : '✓ Получить средства'}
                  </button>
                ) : (
                  <div style={s.unavailableBlock}>
                    <div style={s.unavailableIcon}>ℹ️</div>
                    <div style={s.unavailableText}>
                      {giftInfo.status === 'claimed' || giftInfo.status === 'used'
                        ? 'Этот подарок уже активирован' 
                        : 'Этот подарок недоступен'}
                    </div>
                  </div>
                )}
                
                {!token && isClaimable && (
                  <p style={s.loginHint}>Войдите в аккаунт для активации</p>
                )}
              </div>
            )}

            {/* Uploaded image preview (without gift info) */}
            {uploadedImage && !giftInfo && !decoding && (
              <div style={s.previewWrapper}>
                <img src={uploadedImage} alt="Uploaded" style={s.previewImage} />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Styles
const s = {
  page: {
    minHeight: '100vh',
    padding: '40px 20px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },
  container: {
    maxWidth: 480,
    margin: '0 auto'
  },
  error: {
    background: '#fef2f2',
    color: '#dc2626',
    padding: '12px 16px',
    borderRadius: 12,
    marginBottom: 20,
    fontSize: 14,
    textAlign: 'center'
  },

  // Success Block
  successBlock: {
    background: 'white',
    borderRadius: 24,
    padding: 40,
    textAlign: 'center',
    boxShadow: '0 4px 24px rgba(0,0,0,0.1)'
  },
  successIcon: {
    width: 80,
    height: 80,
    background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 40,
    color: 'white',
    margin: '0 auto 20px'
  },
  successTitle: {
    fontSize: 24,
    fontWeight: 700,
    color: '#1f2937',
    marginBottom: 12
  },
  successAmount: {
    fontSize: 36,
    fontWeight: 800,
    color: '#10B981',
    marginBottom: 8
  },
  successText: {
    fontSize: 15,
    color: '#6b7280',
    marginBottom: 24
  },

  // Upload Section
  uploadSection: {
    background: 'white',
    borderRadius: 24,
    padding: 32,
    textAlign: 'center',
    boxShadow: '0 4px 24px rgba(0,0,0,0.1)',
    marginBottom: 20
  },
  uploadTitle: {
    fontSize: 24,
    fontWeight: 700,
    color: '#1f2937',
    marginBottom: 8
  },
  uploadDesc: {
    fontSize: 15,
    color: '#6b7280',
    marginBottom: 24,
    lineHeight: 1.5
  },
  uploadLabel: {
    display: 'block',
    cursor: 'pointer'
  },
  uploadBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    padding: '16px 32px',
    background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
    color: 'white',
    borderRadius: 14,
    fontSize: 16,
    fontWeight: 600,
    transition: 'all 0.2s',
    minWidth: 200
  },
  uploadBtnDecoding: {
    background: '#9ca3af'
  },
  spinner: {
    width: 18,
    height: 18,
    border: '2px solid rgba(255,255,255,0.3)',
    borderTopColor: 'white',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite'
  },

  // Gift Card
  giftCard: {
    background: 'white',
    borderRadius: 24,
    padding: 24,
    boxShadow: '0 4px 24px rgba(0,0,0,0.1)',
    textAlign: 'center'
  },
  giftImageWrapper: {
    width: 120,
    height: 120,
    margin: '0 auto 20px',
    borderRadius: 16,
    overflow: 'hidden',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
  },
  giftImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover'
  },
  giftInfo: {
    marginBottom: 20
  },
  giftAmount: {
    fontSize: 48,
    fontWeight: 800,
    color: '#10B981',
    lineHeight: 1
  },
  giftCurrency: {
    fontSize: 24,
    fontWeight: 700,
    color: '#10B981',
    marginBottom: 12
  },
  giftStatus: {
    display: 'inline-block',
    padding: '8px 20px',
    borderRadius: 20,
    fontSize: 14,
    fontWeight: 600
  },

  // Buttons
  btnPrimary: {
    padding: '14px 32px',
    background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
    color: 'white',
    border: 'none',
    borderRadius: 12,
    fontSize: 16,
    fontWeight: 600,
    cursor: 'pointer'
  },
  btnClaim: {
    width: '100%',
    padding: '16px',
    background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
    color: 'white',
    border: 'none',
    borderRadius: 14,
    fontSize: 16,
    fontWeight: 700,
    cursor: 'pointer',
    marginTop: 16
  },
  btnDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed'
  },

  // Unavailable Block
  unavailableBlock: {
    background: '#f9fafb',
    borderRadius: 16,
    padding: 20,
    marginTop: 16,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: 8
  },
  unavailableIcon: {
    fontSize: 32
  },
  unavailableText: {
    fontSize: 15,
    color: '#6b7280',
    textAlign: 'center'
  },

  loginHint: {
    fontSize: 13,
    color: '#9ca3af',
    marginTop: 12
  },

  // Preview
  previewWrapper: {
    marginTop: 20,
    borderRadius: 16,
    overflow: 'hidden',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
  },
  previewImage: {
    width: '100%',
    display: 'block'
  }
};

// Add CSS animation
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;
if (!document.getElementById('claim-voucher-styles')) {
  styleSheet.id = 'claim-voucher-styles';
  document.head.appendChild(styleSheet);
}

export default ClaimVoucher;